<!--
  ============================================================================
  MadriLocalGracias — Página de obrigado da campanha /madri-local (v1 e v2).
  ============================================================================
  Recebe o lead após envio do FormularioLead (ActiveCampaign form 43).
  Faz redirect automático para WhatsApp em 7s + botão manual de fallback.

  Diferenças em relação a OligocheckGracias.vue:
    • Idioma único: ES (sem LangSwitcher, sem useIdiomaAuto).
    • Copy contextualizada na demostración presencial en Madrid.
    • Mensagem de WhatsApp explícita sobre a solicitação da visita.
    • Passos seguintes orientados ao agendamento da visita
      (no "vou enviar proposta", mas "vamos coordinar fecha e franja").

  Configurações:
    • Sem useSeo. Disallow em robots.txt (/madri-local/gracias).
    • Recupera `?nome=...` da query (passado pelo FormularioLead).
  ============================================================================
-->
<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    :mostrar-switcher="false"
    :auto-idioma="false"
  >
    <div class="obrigado-page">
      <div class="obrigado-bg" aria-hidden="true"></div>

      <main class="obrigado-card">
        <div class="check-anim">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="m9 12 2 2 4-4"/>
          </svg>
        </div>

        <div class="badge-sucesso">Solicitud recibida</div>

        <h1 class="titulo">
          ¡Gracias<span v-if="nome">, {{ nome }}</span>!
          <br />
          <span class="ds-texto-gradiente">Su solicitud está en camino</span>
        </h1>

        <p class="subtitulo">
          Recibimos su solicitud para la demostración presencial en Madrid.
          En segundos será redirigido a WhatsApp para coordinar la fecha y
          la franja horaria de la visita del especialista a su consulta.
        </p>

        <!-- Contagem regressiva para redirect automático -->
        <div class="contagem-wrapper" :class="{ 'redirecting': isRedirecting }">
          <div class="contagem-circulo">
            <svg class="contagem-svg" viewBox="0 0 100 100">
              <circle class="contagem-track" cx="50" cy="50" r="45" />
              <circle
                class="contagem-progress"
                cx="50"
                cy="50"
                r="45"
                :style="{ strokeDashoffset: progressOffset }"
              />
            </svg>
            <div class="contagem-numero">
              <span v-if="!isRedirecting">{{ countdown }}</span>
              <div v-else class="spinner-grande"></div>
            </div>
          </div>

          <p class="contagem-texto">
            <span v-if="!isRedirecting">
              Será redirigido en <strong>{{ countdown }}s</strong> a WhatsApp...
            </span>
            <span v-else>Abriendo WhatsApp...</span>
          </p>
        </div>

        <!-- Botão manual de WhatsApp (fallback caso o auto-redirect falhe) -->
        <a
          :href="whatsappUrl"
          target="_blank"
          rel="noopener"
          class="botao-whatsapp"
          @click="handleManualClick"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Abrir WhatsApp ahora
        </a>

        <!-- Passos seguintes (orientados ao agendamento presencial) -->
        <div class="passos">
          <div class="passos-titulo">Próximos pasos</div>
          <ul class="passos-lista">
            <li>
              <div class="passo-num">1</div>
              <span>Se abrirá una conversación en WhatsApp con nuestro equipo en Madrid.</span>
            </li>
            <li>
              <div class="passo-num">2</div>
              <span>Coordinaremos fecha, hora y dirección de la demostración técnica en su consulta.</span>
            </li>
            <li>
              <div class="passo-num">3</div>
              <span>El especialista se desplazará a su consulta con el equipo OligoCheck y los informes de muestra.</span>
            </li>
          </ul>
        </div>
      </main>
    </div>

    <template #rodape>
      <RodapeSite
        marca="OligoCheck"
        texto="Análisis mineral intracelular en tiempo real. Tecnología profesional para consultas sanitarias."
        direitos="Todos los derechos reservados."
      />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import AppShell from '@/components/global/layout/AppShell.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'

const route = useRoute()

/* Idioma fixo ES — campanha exclusiva Madrid. */
const currentLang = ref('es')

/* Nome do lead vindo da query string (FormularioLead.handleSubmit
   passa `?nome=<primeiroNome>` no router.push). */
const nome = computed(() => {
  const n = route.query.nome
  return typeof n === 'string' ? n : ''
})

/* ============================================================
   WhatsApp dedicado para a campanha Madrid.
   Mensagem explicita a origem (demostración presencial en Madrid)
   para que o atendente saiba qual fluxo conduzir.
   ============================================================ */
const whatsappComercial = '34600252405'
const whatsappUrl = computed(() => {
  const msg = encodeURIComponent(
    `Hola${nome.value ? `, soy ${nome.value}` : ''}. Acabo de solicitar la demostración presencial de OligoCheck en Madrid y me gustaría coordinar la fecha y la franja de la visita.`
  )
  return `https://api.whatsapp.com/send/?phone=${whatsappComercial}&text=${msg}`
})

/* ============================================================
   Contagem regressiva + redirect automático para WhatsApp
   ============================================================ */
const TEMPO_TOTAL = 7
const countdown = ref(TEMPO_TOTAL)
const isRedirecting = ref(false)
let timer = null

const progressOffset = computed(() => {
  const CIRC = 2 * Math.PI * 45
  const progress = (TEMPO_TOTAL - countdown.value) / TEMPO_TOTAL
  return CIRC * (1 - progress)
})

const iniciarContagem = () => {
  timer = setInterval(() => {
    if (countdown.value > 1) {
      countdown.value -= 1
    } else {
      countdown.value = 0
      clearInterval(timer)
      redirecionar()
    }
  }, 1000)
}

const redirecionar = () => {
  if (isRedirecting.value) return
  isRedirecting.value = true
  setTimeout(() => {
    window.location.href = whatsappUrl.value
  }, 600)
}

const handleManualClick = () => {
  if (timer) clearInterval(timer)
  isRedirecting.value = true
}

onMounted(() => {
  iniciarContagem()
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
/* ============================================
   BASE — layout centralizado vertical
   ============================================ */
.obrigado-page {
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-s-8) var(--ds-s-5);
  position: relative;
  overflow-x: hidden;
  overflow-y: visible;
}

.obrigado-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 20%, rgba(20, 203, 240, 0.12) 0%, transparent 45%),
    radial-gradient(circle at 80% 80%, rgba(37, 211, 102, 0.08) 0%, transparent 45%);
  pointer-events: none;
}

/* ============================================
   CARD CENTRAL
   ============================================ */
.obrigado-card {
  position: relative;
  z-index: 1;
  max-width: 560px;
  width: 100%;
  background: rgba(10, 26, 42, 0.5);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-2xl);
  padding: var(--ds-s-8) 32px var(--ds-s-7);
  text-align: center;
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.6);
  animation: obrigadoFadeInUp 0.7s ease-out;
}

@keyframes obrigadoFadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ============================================
   CHECK ANIMADO + BADGE DE SUCESSO
   ============================================ */
.check-anim {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: var(--ds-success-soft);
  border: 2px solid rgba(37, 211, 102, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ds-success);
  margin: 0 auto var(--ds-s-6);
  animation: checkPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 0 40px rgba(37, 211, 102, 0.25);
}

@keyframes checkPop {
  0% { transform: scale(0); opacity: 0; }
  80% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.badge-sucesso {
  display: inline-block;
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-success);
  background: var(--ds-success-soft);
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 18px;
}

.titulo {
  font-family: var(--ds-font-display);
  font-size: 30px;
  line-height: 1.2;
  margin: 0 0 14px;
  color: var(--ds-text);
  letter-spacing: -0.5px;
}

.subtitulo {
  font-size: 15px;
  color: var(--ds-text-muted);
  line-height: 1.6;
  margin: 0 0 var(--ds-s-7);
}

/* ============================================
   CONTAGEM REGRESSIVA
   ============================================ */
.contagem-wrapper {
  margin-bottom: 28px;
  transition: opacity 0.3s ease;
}

.contagem-wrapper.redirecting {
  opacity: 0.75;
}

.contagem-circulo {
  position: relative;
  width: 110px;
  height: 110px;
  margin: 0 auto 14px;
}

.contagem-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.contagem-track {
  fill: none;
  stroke: var(--ds-border);
  stroke-width: 6;
}

.contagem-progress {
  fill: none;
  stroke: var(--ds-accent);
  stroke-width: 6;
  stroke-linecap: round;
  stroke-dasharray: 282.743;
  transition: stroke-dashoffset 1s linear;
  filter: drop-shadow(0 0 6px rgba(20, 203, 240, 0.5));
}

.contagem-numero {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--ds-font-display);
  font-size: 42px;
  color: var(--ds-accent);
  letter-spacing: -1px;
}

.spinner-grande {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(20, 203, 240, 0.25);
  border-top-color: var(--ds-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.contagem-texto {
  font-size: 14px;
  color: var(--ds-text-muted);
  margin: 0;
  line-height: 1.5;
}

.contagem-texto strong {
  color: var(--ds-text);
  font-family: var(--ds-font-bold);
}

/* ============================================
   BOTÃO WHATSAPP MANUAL
   ============================================ */
.botao-whatsapp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-s-3);
  background: var(--ds-gradient-success);
  color: var(--ds-text);
  text-decoration: none;
  font-family: var(--ds-font-bold);
  font-size: 15px;
  padding: 15px 28px;
  border-radius: var(--ds-r-md);
  box-shadow: 0 10px 28px -6px rgba(37, 211, 102, 0.55);
  transition: all var(--ds-t-smooth);
  width: 100%;
  max-width: 360px;
  margin-bottom: var(--ds-s-7);
}

.botao-whatsapp:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 34px -6px rgba(37, 211, 102, 0.7);
  filter: brightness(1.05);
}

/* ============================================
   PASSOS
   ============================================ */
.passos {
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: 18px;
  padding: 22px var(--ds-s-5);
  text-align: left;
}

.passos-titulo {
  font-family: var(--ds-font-bold);
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
  margin-bottom: 14px;
  text-align: center;
}

.passos-lista {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--ds-s-3);
}

.passos-lista li {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-s-3);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
}

.passo-num {
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--ds-font-bold);
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 1px;
  border: 1px solid var(--ds-accent-border);
}

/* ============================================
   RESPONSIVO
   ============================================ */
@media (max-width: 600px) {
  .obrigado-card { padding: var(--ds-s-7) 20px var(--ds-s-6); }
  .titulo { font-size: 24px; }
  .subtitulo { font-size: 14px; }
  .contagem-circulo { width: 96px; height: 96px; }
  .contagem-numero { font-size: 36px; }
  .botao-whatsapp { font-size: 14px; padding: 14px 22px; }
}
</style>
